import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  GlobeAltIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { farmApi, alertsApi, indicesApi, weatherApi } from '../lib/api';
import { format, subDays } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { StatCard, StatCardSkeleton, Card, CardHeader, CardTitle, Button, EmptyState } from '../components/ui';
import { FarmCardSkeleton, ChartSkeleton } from '../components/ui/Skeleton';

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string; color: string }[]; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-strong rounded-lg px-3 py-2 shadow-soft-md">
        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { data: farms = [], isLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: alertData } = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: alertsApi.getSummary,
  });

  // Fetch NDVI data for the first farm (or aggregate)
  const firstFarmId = farms[0]?.id;
  const { data: ndviApiData } = useQuery({
    queryKey: ['dashboard-ndvi', firstFarmId],
    queryFn: async () => {
      if (!firstFarmId) return null;
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = subDays(new Date(), 30).toISOString().split('T')[0];
      try {
        const result = await indicesApi.getTimeSeries(firstFarmId, 'ndvi', startDate, endDate);
        return result.data || [];
      } catch {
        return [];
      }
    },
    enabled: !!firstFarmId,
  });

  // Fetch weather forecast for the first farm
  const { data: weatherApiData } = useQuery({
    queryKey: ['dashboard-weather', firstFarmId],
    queryFn: async () => {
      if (!firstFarmId) return null;
      try {
        const result = await weatherApi.getForecast(firstFarmId, 7);
        return result.data || result.forecast || [];
      } catch {
        return [];
      }
    },
    enabled: !!firstFarmId,
  });

  // Transform NDVI data for chart
  const ndviData = ndviApiData && ndviApiData.length > 0
    ? ndviApiData.map((item: { date: string; meanValue: number }) => ({
        date: format(new Date(item.date), 'MMM d'),
        ndvi: item.meanValue,
      }))
    : Array.from({ length: 30 }, (_, i) => ({
        date: format(subDays(new Date(), 29 - i), 'MMM d'),
        ndvi: null, // No data placeholder
      }));

  // Transform weather data for chart
  const weatherData = weatherApiData && weatherApiData.length > 0
    ? weatherApiData.map((item: { date: string; temperatureMax: number; precipitation: number }) => ({
        date: format(new Date(item.date), 'EEE'),
        temp: Math.round(item.temperatureMax),
        rain: Math.round(item.precipitation || 0),
      }))
    : Array.from({ length: 7 }, (_, i) => ({
        date: format(subDays(new Date(), 6 - i), 'EEE'),
        temp: null,
        rain: null,
      }));

  // Calculate average NDVI from API data
  const avgNdvi = ndviApiData && ndviApiData.length > 0
    ? (ndviApiData.reduce((sum: number, item: { meanValue: number }) => sum + item.meanValue, 0) / ndviApiData.length).toFixed(2)
    : 'N/A';

  const totalArea = farms.reduce((sum, f) => sum + (f.areaHectares || 0), 0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-kenagri-800 via-kenagri-700 to-kenagri-800 p-6 lg:p-8">
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-kenagri-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-kenagri-400/10 rounded-full blur-2xl" />

        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'Farmer'}!
            </h1>
            <p className="mt-2 text-kenagri-100/80 max-w-xl">
              {farms.length > 0
                ? "Here's your latest monitoring overview and crop insights."
                : "Get started by adding your first farm to monitor."}
            </p>
          </div>
          <Link to="/farms/new">
            <Button
              variant="primary"
              size="lg"
              leftIcon={<PlusIcon className="h-5 w-5" />}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 shadow-none hover:shadow-none"
            >
              Add Farm
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Farms"
              value={farms.length}
              icon={GlobeAltIcon}
              color="green"
            />
            <StatCard
              title="Total Area"
              value={`${totalArea.toFixed(1)} ha`}
              icon={ChartBarIcon}
              color="blue"
            />
            <StatCard
              title="Avg. NDVI"
              value={avgNdvi}
              icon={ArrowTrendingUpIcon}
              color="purple"
            />
            <StatCard
              title="Active Alerts"
              value={alertData?.unacknowledged || 0}
              icon={ExclamationTriangleIcon}
              color={alertData?.unacknowledged > 0 ? 'amber' : 'green'}
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {isLoading ? (
          <>
            <ChartSkeleton />
            <ChartSkeleton />
          </>
        ) : (
          <>
            {/* NDVI Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>NDVI Trend (30 Days)</CardTitle>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  {farms.length > 0 ? farms[0].name : 'No Farm'}
                </span>
              </CardHeader>
              <div className="h-64">
                {ndviApiData && ndviApiData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ndviData}>
                      <defs>
                        <linearGradient id="ndviGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        domain={[0, 1]}
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="ndvi"
                        name="NDVI"
                        stroke="#16a34a"
                        strokeWidth={2}
                        fill="url(#ndviGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <p>No NDVI data available. Add a farm and run analysis.</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Weather Chart */}
            <Card variant="gradient">
              <CardHeader>
                <CardTitle>Weather Forecast (7 Days)</CardTitle>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  {farms.length > 0 ? 'Local Area' : 'No Location'}
                </span>
              </CardHeader>
              <div className="h-64">
                {weatherApiData && weatherApiData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weatherData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ paddingTop: 16 }}
                      />
                      <Bar dataKey="temp" name="Temp (°C)" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="rain" name="Rain (mm)" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400">
                    <p>No weather data available. Add a farm to get forecasts.</p>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Farm List */}
      <Card variant="gradient">
        <CardHeader>
          <CardTitle>Your Farms</CardTitle>
          <Link
            to="/farms"
            className="flex items-center gap-1 text-sm font-medium text-kenagri-600 hover:text-kenagri-700 transition-colors"
          >
            View All
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </CardHeader>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FarmCardSkeleton />
            <FarmCardSkeleton />
            <FarmCardSkeleton />
          </div>
        ) : farms.length > 0 ? (
          <div className="space-y-2">
            {farms.slice(0, 5).map((farm) => (
              <FarmListItem key={farm.id} farm={farm} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={GlobeAltIcon}
            title="No farms yet"
            description="Add your first farm to start monitoring your crops with satellite imagery."
            action={{
              label: 'Add Your First Farm',
              href: '/farms/new',
            }}
          />
        )}
      </Card>
    </div>
  );
}

// Separate component to fetch individual farm NDVI
function FarmListItem({ farm }: { farm: { id: string; name: string; areaHectares: number; cropType: string | null } }) {
  const { data: farmStats } = useQuery({
    queryKey: ['farm-stats', farm.id],
    queryFn: async () => {
      try {
        return await farmApi.getStats(farm.id);
      } catch {
        return null;
      }
    },
  });

  const ndviValue = farmStats?.latestNdvi?.meanValue ?? null;
  const ndviPercent = ndviValue !== null ? Math.round(ndviValue * 100) : 0;

  return (
    <Link
      to={`/farms/${farm.id}`}
      className="flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 group transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-kenagri-100 to-kenagri-50 flex items-center justify-center">
          <GlobeAltIcon className="h-6 w-6 text-kenagri-600" />
        </div>
        <div>
          <p className="font-medium text-gray-900 group-hover:text-kenagri-600 transition-colors">
            {farm.name}
          </p>
          <p className="text-sm text-gray-500">
            {farm.areaHectares?.toFixed(1)} ha • {farm.cropType || 'Unknown crop'}
          </p>
        </div>
      </div>
      <div className="text-right">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">NDVI</span>
          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-kenagri-400 to-kenagri-600 rounded-full transition-all duration-500"
              style={{ width: ndviValue !== null ? `${ndviPercent}%` : '0%' }}
            />
          </div>
          <span className="text-sm font-semibold text-kenagri-600">
            {ndviValue !== null ? ndviValue.toFixed(2) : 'N/A'}
          </span>
        </div>
      </div>
    </Link>
  );
}
